import { LitElement, ReactiveController, ReactiveControllerHost } from 'lit';

/** What the controller needs from the sidebar. Keeps navigation state on the host. */
export interface SidebarFocusHost {
  /** Pinned panels are persistent layout, not dismissible flyouts. */
  readonly pinned: boolean;
  /** Whether a second-level panel is currently open. */
  readonly panelOpen: boolean;
  /** Clear the open-panel state. */
  closePanel(): void;
  /** The live panel element, or null when none is rendered. */
  queryPanel(): { focusFirstItem(): boolean; whenContentSettled(): Promise<void> } | null;
  /** The tier-1 trigger whose panel is open, for focus restoration. */
  queryOpenTrigger(): HTMLElement | null;
}

/**
 * Owns *where focus is* for the two-tier sidebar navigation, per the ARIA APG
 * Disclosure Navigation pattern:
 *
 * - keyboard activation of a tier-1 trigger moves focus into the panel;
 *   pointer activation leaves focus alone
 * - Escape closes an unpinned panel and returns focus to its trigger
 * - focus leaving the sidebar entirely closes an unpinned panel
 *
 * Holds no navigation state — it reads and mutates that through SidebarFocusHost.
 */
export class SidebarPanelFocusController implements ReactiveController {
  private host: SidebarFocusHost & ReactiveControllerHost & LitElement;

  /** Set when the last tier-1 activation came from the keyboard. */
  private _viaKeyboard = false;
  /** Armed while we still owe the panel a focus move. */
  private _pendingFocus = false;
  /** The element focus sat on when the retry was armed; guards against stealing focus. */
  private _pendingFrom: Element | null = null;
  private _listening = false;

  constructor(host: SidebarFocusHost & ReactiveControllerHost & LitElement) {
    this.host = host;
    host.addController(this);
  }

  hostConnected() {
    if (!this._listening) {
      this._listening = true;
      document.addEventListener('focusin', this.onDocumentFocusIn);
    }
  }

  hostDisconnected() {
    this._listening = false;
    document.removeEventListener('focusin', this.onDocumentFocusIn);
    this.clearPending();
  }

  /** Record activation modality. Keyboard-synthesized clicks report detail === 0. */
  noteActivation(event: MouseEvent) {
    this._viaKeyboard = event.detail === 0;
  }

  /**
   * Ask for focus to move into the panel, honouring the recorded modality.
   *
   * A pointer activation must also cancel any *outstanding* request: a prior
   * keyboard activation can still be waiting on a slow settle (e.g. a still-
   * loading menu) when the user closes and reopens via mouse, and without
   * this, the stale request stays armed and its `_pendingFrom` can still
   * match the (unmoved) active element on the very trigger being reopened —
   * moving focus into the panel on a pure pointer interaction.
   */
  requestPanelFocus() {
    if (!this._viaKeyboard) {
      this.clearPending();
      return;
    }
    this._pendingFocus = true;
    this._pendingFrom = this.deepActiveElement();
  }

  /** Escape closes an unpinned panel and hands focus back to its trigger. */
  handleKeydown(event: KeyboardEvent) {
    if (event.key !== 'Escape' || !this.host.panelOpen || this.host.pinned) {
      return;
    }
    // Capture before closing: the state change re-renders the rail.
    const trigger = this.host.queryOpenTrigger();
    this.host.closePanel();
    this.clearPending();
    void this.host.updateComplete.then(() => trigger?.focus());
  }

  /**
   * Attempt the pending focus move, once the panel's content has genuinely
   * settled. Panel content arrives asynchronously (the first attempt often
   * finds a Loading… panel with no links), and Lit's `updateComplete` does not
   * await child element updates — a freshly rendered `platform-nav-section`
   * finishing its own render never triggers another update here, so a blind
   * per-host-update retry can attempt focus before the anchors are assignable
   * to a slot. Waiting on the panel's own `whenContentSettled()` instead means
   * each attempt only ever runs once the content is actually in the flattened,
   * focusable tree.
   *
   * Runs unconditionally on every host update while a focus move is owed, with
   * no single-flight guard: overlapping settle-and-attempt cycles are harmless
   * (a second `focusFirstItem()` against already-focused content just reports
   * `true` again, and clearing pending twice is a no-op), whereas coalescing
   * them risks dropping the one host update that actually carries the menu
   * data. A static or already-cached product menu resolves within a handful of
   * microtasks of the update that opened the panel — comfortably inside an
   * earlier settle's own window — so a guard here would gate the whole retry
   * on unguaranteed microtask interleaving.
   */
  hostUpdated() {
    if (!this._pendingFocus) {
      return;
    }
    // The panel closed: nothing to attempt, and nothing must survive to a
    // later reopen. Checked before the `!panel` query below, because a closed
    // panel is also an unrendered one — without this, closing (by any path
    // that doesn't itself call requestPanelFocus()) leaves pending armed
    // forever, ready to fire the moment a panel next exists and happens to
    // still carry a matching `_pendingFrom`.
    if (!this.host.panelOpen) {
      this.clearPending();
      return;
    }
    const panel = this.host.queryPanel();
    if (!panel) {
      return;
    }
    void panel
      .whenContentSettled()
      .then(() => {
        if (!this._pendingFocus) {
          return;
        }
        // Abandon if the panel closed, or if focus already moved on — late-arriving
        // menu data must never yank focus from a user who has gone elsewhere.
        if (!this.host.panelOpen || this.deepActiveElement() !== this._pendingFrom) {
          this.clearPending();
          return;
        }
        if (panel.focusFirstItem()) {
          this.clearPending();
        }
      })
      .catch(() => {
        // whenContentSettled() rejects if a render throws anywhere in the
        // panel/section/sl-details chain. Swallow it here instead of leaving
        // an unhandled rejection: pending stays armed, and the next host
        // update starts its own, independent settle — nothing here can wedge
        // the retry permanently.
      });
  }

  private clearPending() {
    this._pendingFocus = false;
    this._pendingFrom = null;
  }

  /**
   * Close an unpinned panel once focus leaves the sidebar. Containment is tested
   * against the whole sidebar, not the panel: activating a trigger focuses that
   * trigger, which sits outside the panel, so a panel-only test would slam the
   * panel shut the instant a pointer user opened it. Testing the sidebar also
   * lets Shift+Tab return to the trigger with the panel still open.
   */
  private onDocumentFocusIn = (event: FocusEvent) => {
    if (!this.host.panelOpen || this.host.pinned) {
      return;
    }
    if (!event.composedPath().includes(this.host)) {
      this.host.closePanel();
    }
  };

  /** activeElement, walked down through shadow roots. */
  private deepActiveElement(): Element | null {
    let el: Element | null = document.activeElement;
    while (el?.shadowRoot?.activeElement) {
      el = el.shadowRoot.activeElement;
    }
    return el;
  }
}
